import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader as Loader2, Mail, Lock } from 'lucide-react';
import { useWorkspace } from '../context/WorkspaceContext';

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { publicWorkspace } = useWorkspace();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loginMode, setLoginMode] = useState<'magic' | 'password'>('magic');

  const branding = publicWorkspace?.appearance?.loginPage || {};
  
  // Detect whether the workspace uses a light background so we can pick
  // appropriate default contrasting colours for the card.
  const bgColor = branding.backgroundColor || '#18181b';
  const isLightBg = isLightColor(bgColor);
  
  const logo = branding.logo || publicWorkspace?.logo || (isLightBg ? '/icon-blue.svg' : '/icon-white.svg');
  const title = branding.title || 'Welcome Back';
  const subtitle = branding.subtitle || `Sign in to continue to ${publicWorkspace?.name || 'AgencyPlus'}`;

  // Card colours adapt to the page background
  const cardBg    = isLightBg ? 'rgba(255,255,255,0.85)' : 'rgba(39,39,42,0.85)';
  const cardBorder = isLightBg ? 'rgba(0,0,0,0.10)'     : 'rgba(255,255,255,0.08)';
  const labelColor = branding.textColor || (isLightBg ? '#1f2937' : '#e4e4e7');
  const inputBg    = isLightBg ? 'rgba(243,244,246,0.8)' : 'rgba(9,9,11,0.5)';
  const inputBorder = isLightBg ? 'rgba(0,0,0,0.15)'    : 'rgba(113,113,122,0.6)';
  const inputText  = isLightBg ? '#111827' : '#ffffff';
  const inputPlaceholder = isLightBg ? '#9ca3af' : '#71717a';
  const mutedText  = isLightBg ? 'rgba(55,65,81,0.7)' : '#a1a1aa';
  const accentColor = branding.buttonColor || '#3b82f6';
  const footerBorderColor = isLightBg ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.05)';

  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam === 'no_workspace') {
      setError('Your account is not associated with any active workspace. Please contact support or sign up for a new account.');
    }
  }, [searchParams]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (loginMode === 'magic') {
        // Securely check if the user exists via the RPC we created
        const { data: userExists, error: rpcError } = await supabase.rpc('check_user_exists', { p_email: email });
        
        // If the RPC is available and returns false, the user definitely does not exist
        if (!rpcError && userExists === false) {
          throw new Error('No account found with this email address. Please sign up first.');
        }

        const { error: otpError } = await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: window.location.origin + '/home',
            shouldCreateUser: false,
          },
        });

        if (otpError) {
          console.error('Magic link OTP error:', otpError);
          if (
            otpError.message.toLowerCase().includes('signups not allowed') ||
            otpError.message.toLowerCase().includes('user not found')
          ) {
            throw new Error('No account found with this email address. Please sign up first.');
          }
          throw otpError;
        }
        setSuccess('Check your email for the magic login link!');
        setLoading(false);
        return;
      }

      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) throw signInError;

      if (data.user) {
        // Check for active workspace memberships
        const { data: memberData, error: memberError } = await supabase
          .from('workspace_members')
          .select('id')
          .eq('user_id', data.user.id)
          .eq('status', 'active')
          .limit(1);

        if (memberError) {
          console.error('Error checking memberships:', memberError);
        }

        if (!memberData || memberData.length === 0) {
          // No workspaces found. Check if this is a brand new user (rare at this step due to trigger)
          // or an orphan user who should be blocked.
          await supabase.auth.signOut();
          setError('Your account is not associated with any active workspace. Please contact support or sign up for a new account.');
          setLoading(false);
          return;
        }

        const redirect = searchParams.get('redirect');
        if (redirect) {
          navigate(decodeURIComponent(redirect));
        } else {
          navigate('/home');
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error('Login error details:', err);
        setError(err.message || 'Error sending magic link email');
      } else {
        setError('An unexpected error occurred');
      }
      setLoading(false);
    } finally {
      if (loginMode === 'password') {
        setLoading(false);
      }
    }
  };

  return (
    <div
      className="h-[100dvh] sm:h-auto sm:min-h-screen w-full overflow-y-auto flex flex-col p-4 transition-colors duration-500"
      style={{
        backgroundColor: bgColor,
        backgroundImage: branding.backgroundImage ? `url(${branding.backgroundImage})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="w-full max-w-md m-auto py-6">
        {/* Logo + heading */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img src={logo} alt={publicWorkspace?.name || 'AgencyPlus'} className="h-20 w-auto object-contain max-h-20" />
          </div>
          <h1 className="text-3xl font-bold mb-2" style={{ color: branding.textColor || (isLightBg ? '#111827' : '#ffffff') }}>
            {title}
          </h1>
          <p style={{ color: mutedText }}>{subtitle}</p>
        </div>

        {/* Card */}
        <div
          className="rounded-xl shadow-xl overflow-hidden backdrop-blur-sm"
          style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
        >
          {/* Card Header */}
          <div className="px-6 pt-6 pb-2">
            <h2 className="text-xl font-semibold" style={{ color: labelColor }}>
              {loginMode === 'magic' ? 'Magic Login' : 'Sign In'}
            </h2>
            <p className="text-sm mt-1" style={{ color: mutedText }}>
              {loginMode === 'magic' 
                ? 'Enter your email to receive a passwordless login link' 
                : 'Enter your credentials to access your account'}
            </p>
          </div>

          {/* Card Body */}
          <div className="px-6 py-4">
            <form onSubmit={handleLogin} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {success && (
                <Alert className="bg-green-500/10 border-green-500/50 text-green-500">
                  <AlertDescription>{success}</AlertDescription>
                </Alert>
              )}

              {/* Email */}
              <div className="space-y-1.5">
                <label htmlFor="email" className="block text-sm font-medium" style={{ color: labelColor }}>Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: inputPlaceholder }} />
                  <input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading || !!success}
                    className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm outline-none transition-all"
                    style={{
                      background: inputBg,
                      border: `1px solid ${inputBorder}`,
                      color: inputText,
                    }}
                  />
                </div>
              </div>

              {/* Password (only in password mode) */}
              {loginMode === 'password' && (
                <div className="space-y-1.5 animate-in fade-in slide-in-from-top-2 duration-300">
                  <label htmlFor="password" className="block text-sm font-medium" style={{ color: labelColor }}>Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: inputPlaceholder }} />
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                      className="w-full pl-10 pr-10 py-2.5 rounded-lg text-sm outline-none transition-all"
                      style={{
                        background: inputBg,
                        border: `1px solid ${inputBorder}`,
                        color: inputText,
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs"
                      style={{ color: inputPlaceholder }}
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  <div className="flex items-center justify-end">
                    <Link
                      to="/reset-password"
                      className="text-sm font-medium hover:opacity-80 transition-opacity"
                      style={{ color: accentColor }}
                    >
                      Forgot password?
                    </Link>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !!success}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold text-white transition-all active:scale-[0.98] disabled:opacity-60"
                style={{ backgroundColor: accentColor }}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {loginMode === 'magic' ? 'Sending Link…' : 'Signing in…'}
                  </>
                ) : (
                  loginMode === 'magic' ? 'Send Magic Link' : 'Sign In'
                )}
              </button>

              <div className="pt-2 text-center">
                <button
                  type="button"
                  onClick={() => {
                    setLoginMode(loginMode === 'magic' ? 'password' : 'magic');
                    setError(null);
                    setSuccess(null);
                  }}
                  className="text-sm font-medium hover:opacity-80 transition-opacity"
                  style={{ color: accentColor }}
                >
                  {loginMode === 'magic' ? 'Or login with password' : 'Or use magic link'}
                </button>
              </div>
            </form>
          </div>

          {/* Card Footer */}
          <div
            className="px-6 py-4 flex justify-center"
            style={{ borderTop: `1px solid ${footerBorderColor}` }}
          >
            <p className="text-sm" style={{ color: mutedText }}>
              Don't have an account?{' '}
              <Link to="/signup" className="font-semibold hover:opacity-80 transition-opacity" style={{ color: accentColor }}>
                Sign up
              </Link>
            </p>
          </div>
        </div>

        {/* Powered by Agency+ badge */}
        {publicWorkspace && (
          <p className="text-center text-sm mt-8 flex items-center justify-center gap-1" style={{ color: mutedText }}>
            Powered by{' '}
            <img src={isLightBg ? "/icon-blue.svg" : "/icon-white.svg"} alt="Agency+" className="h-4 w-4 opacity-50" />
            <span className="font-semibold opacity-50 text-xs tracking-tight">AgencyPlus</span>
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * Returns true if a hex/rgb colour is perceptually light
 * so we can choose contrasting defaults for the card.
 */
function isLightColor(color: string): boolean {
  try {
    let r = 0, g = 0, b = 0;
    if (color.startsWith('#')) {
      const hex = color.replace('#', '');
      const full = hex.length === 3
        ? hex.split('').map(c => c + c).join('')
        : hex;
      r = parseInt(full.slice(0, 2), 16);
      g = parseInt(full.slice(2, 4), 16);
      b = parseInt(full.slice(4, 6), 16);
    } else if (color.startsWith('rgb')) {
      const match = color.match(/\d+/g);
      if (match) { r = +match[0]; g = +match[1]; b = +match[2]; }
    }
    // WCAG relative luminance
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luminance > 160; // threshold: > ~63% brightness
  } catch {
    return false;
  }
}
